<template>
  <div class="snies-panel">
    <div class="sp-cab">
      <h4>Registro ante el Ministerio (SNIES)</h4>
      <span class="sp-conteo">{{ registros.length }} registro{{ registros.length === 1 ? '' : 's' }}</span>
      <button class="btn-sp-nuevo" @click="abrirNuevo">+ Registro</button>
    </div>

    <p class="sp-ayuda">
      El ministerio registra por programa, municipio y modalidad, así que un mismo
      programa puede tener varios registros con vencimientos distintos.
    </p>

    <div v-if="cargando" class="sp-info">Cargando registros…</div>
    <p v-else-if="!registros.length" class="sp-vacio">
      Este programa no tiene registros SNIES cargados.
    </p>

    <div v-for="r in registros" :key="r.id_programa_snies" class="sp-registro">
      <div class="sp-reg-cab">
        <span class="sp-codigo">{{ r.codigo_snies }}</span>
        <span class="sp-meta">{{ r.campus?.nombre ?? r.municipio ?? '—' }}</span>
        <span class="sp-meta">{{ r.modalidad ?? '—' }}</span>
        <span class="sp-meta">{{ r.nivel_formacion ?? '—' }}</span>
        <!-- La bandera la calcula el backend a partir de las resoluciones -->
        <span v-if="r.bandera" :class="['sp-bandera', `sp-${r.bandera}`]">{{ textoBandera(r) }}</span>
        <span v-else class="sp-bandera sp-sin-dato" title="Falta la fecha de ejecutoria o la vigencia">
          sin fecha
        </span>
        <div class="sp-reg-acciones">
          <button class="btn-sp" @click="expandido = expandido === r.id_programa_snies ? null : r.id_programa_snies">
            {{ expandido === r.id_programa_snies ? 'Ocultar' : 'Resoluciones' }}
          </button>
          <button class="btn-sp" @click="abrirEditar(r)">Editar</button>
          <button class="btn-sp btn-sp-peligro" @click="eliminar(r)">Eliminar</button>
        </div>
      </div>

      <div class="sp-reg-datos">
        <span v-if="r.vence_registro">Registro calificado vence <strong>{{ formatFecha(r.vence_registro) }}</strong></span>
        <span v-if="r.alta_calidad_vigente" class="sp-acreditado">Acreditado en alta calidad</span>
        <span v-if="r.en_primer_registro" class="sp-primer">En primer registro</span>
        <span v-if="r.anios_funcionamiento !== null">{{ r.anios_funcionamiento }} años en el SNIES</span>
      </div>

      <!-- Historial de resoluciones -->
      <div v-if="expandido === r.id_programa_snies" class="sp-resoluciones">
        <table v-if="r.resoluciones?.length" class="sp-tabla">
          <thead>
            <tr><th>Acto</th><th>Número</th><th>Ejecutoria</th><th>Vigencia</th><th>Vence</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="res in r.resoluciones" :key="res.id_resolucion">
              <td>{{ etiquetaTipoActo(res.tipo_acto) }}</td>
              <td>{{ res.numero ?? '—' }}</td>
              <td>{{ formatFecha(res.fecha_ejecutoria) }}</td>
              <td>{{ res.vigencia_anios ? res.vigencia_anios + ' años' : '—' }}</td>
              <td>
                <span v-if="res.fecha_vencimiento" :class="['sp-vence', res.bandera ? `sp-${res.bandera}` : '']">
                  {{ formatFecha(res.fecha_vencimiento) }}
                </span>
                <span v-else class="sp-nd">no calculable</span>
              </td>
              <td><button class="btn-quitar-sp" @click="quitarResolucion(res.id_resolucion)">×</button></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="sp-vacio">Sin resoluciones registradas.</p>

        <!-- Alta de resolución -->
        <div class="sp-nueva-res">
          <select v-model="nuevaRes.tipo_acto" class="sp-input">
            <option value="" disabled>Tipo de acto…</option>
            <option v-for="t in tiposActo" :key="t" :value="t">{{ etiquetaTipoActo(t) }}</option>
          </select>
          <input v-model="nuevaRes.numero" class="sp-input sp-corto" placeholder="Número" />
          <input v-model="nuevaRes.fecha_ejecutoria" type="date" class="sp-input" title="Fecha de ejecutoria" />
          <input v-model.number="nuevaRes.vigencia_anios" type="number" min="1" max="20"
                 class="sp-input sp-corto" placeholder="Años" />
          <button class="btn-sp btn-sp-ok" :disabled="!nuevaRes.tipo_acto" @click="agregarResolucion(r)">
            Agregar
          </button>
        </div>
        <p class="sp-ayuda-res">
          El vencimiento no se escribe: se calcula como ejecutoria + vigencia.
        </p>
      </div>
    </div>

    <!-- Alta / edición del registro -->
    <div v-if="editando" class="sp-form">
      <h5>{{ editando.id_programa_snies ? 'Editar registro' : 'Nuevo registro SNIES' }}</h5>
      <div class="sp-form-grid">
        <label>Código SNIES *<input v-model="editando.codigo_snies" class="sp-input" /></label>
        <label>Campus
          <select v-model="editando.id_campus" class="sp-input">
            <option :value="null">— Sin campus —</option>
            <option v-for="c in campus" :key="c.id_campus" :value="c.id_campus">{{ c.nombre }}</option>
          </select>
        </label>
        <label>Modalidad
          <select v-model="editando.modalidad" class="sp-input">
            <option :value="null">—</option>
            <option v-for="m in modalidades" :key="m" :value="m">{{ m }}</option>
          </select>
        </label>
        <label>Nivel de formación
          <select v-model="editando.nivel_formacion" class="sp-input">
            <option :value="null">—</option>
            <option v-for="n in nivelesFormacion" :key="n" :value="n">{{ n }}</option>
          </select>
        </label>
        <label>Título otorgado<input v-model="editando.titulo_otorgado" class="sp-input" /></label>
        <label>Estado
          <select v-model="editando.estado" class="sp-input">
            <option value="Activo">Activo</option>
            <option value="Inactivo">Inactivo</option>
          </select>
        </label>
      </div>
      <div class="sp-form-acciones">
        <button class="btn-sp" @click="editando = null">Cancelar</button>
        <button class="btn-sp btn-sp-ok" :disabled="!editando.codigo_snies || guardando" @click="guardar">
          {{ guardando ? 'Guardando…' : 'Guardar' }}
        </button>
      </div>
      <p v-if="errorForm" class="sp-error">{{ errorForm }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Registros SNIES de un programa y sus resoluciones.
 *
 * Registro calificado y acreditación de alta calidad son trámites distintos y
 * vencen por separado.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, watch, onMounted } from 'vue'
import {
  listarProgramasSniesService,
  crearProgramaSniesService,
  actualizarProgramaSniesService,
  eliminarProgramaSniesService,
  agregarResolucionService,
  eliminarResolucionService,
  getCatalogosSniesService,
  etiquetaTipoActo,
  type ProgramaSnies
} from '@/services/ProgramaSniesService'
import { useGestionCampus } from '@/store/GestionCampus'

const props = defineProps<{ idPrograma: number }>()

const campusStore = useGestionCampus()
const registros = ref<ProgramaSnies[]>([])
const cargando = ref(false)
const guardando = ref(false)
const expandido = ref<number | null>(null)
const editando = ref<Partial<ProgramaSnies> | null>(null)
const errorForm = ref('')

const tiposActo = ref<string[]>([])
const modalidades = ref<string[]>([])
const nivelesFormacion = ref<string[]>([])
const campus = ref<Array<{ id_campus: number; nombre: string }>>([])

const nuevaRes = ref({ tipo_acto: '', numero: '', fecha_ejecutoria: '', vigencia_anios: null as number | null })

function formatFecha(valor: string | null | undefined): string {
  if (!valor) return '—'
  const d = new Date(valor.includes('T') ? valor : valor + 'T00:00:00')
  if (isNaN(d.getTime())) return valor
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Texto de la bandera: lo urgente se dice con palabras, no solo con color. */
function textoBandera(r: ProgramaSnies): string {
  const res = r.resoluciones?.find(x => x.bandera === r.bandera)
  const meses = res?.meses_para_vencer
  if (meses == null) return r.bandera ?? ''
  if (meses < 0) return `vencido hace ${Math.abs(meses)} meses`
  return `vence en ${meses} meses`
}

async function cargar() {
  cargando.value = true
  try {
    registros.value = await listarProgramasSniesService({ id_programa: props.idPrograma })
  } finally {
    cargando.value = false
  }
}

function abrirNuevo() {
  errorForm.value = ''
  editando.value = {
    codigo_snies: '', id_programa: props.idPrograma, id_campus: null,
    modalidad: null, nivel_formacion: null, titulo_otorgado: '', estado: 'Activo'
  }
}

function abrirEditar(r: ProgramaSnies) {
  errorForm.value = ''
  editando.value = { ...r }
}

async function guardar() {
  if (!editando.value?.codigo_snies) return
  guardando.value = true
  errorForm.value = ''
  try {
    const payload = { ...editando.value, id_programa: props.idPrograma }
    const resultado = editando.value.id_programa_snies
      ? await actualizarProgramaSniesService(editando.value.id_programa_snies, payload)
      : await crearProgramaSniesService(payload)

    if (!resultado.ok) {
      errorForm.value = resultado.error ?? 'No se pudo guardar'
      return
    }
    editando.value = null
    await cargar()
  } finally {
    guardando.value = false
  }
}

async function eliminar(r: ProgramaSnies) {
  const resultado = await eliminarProgramaSniesService(r.id_programa_snies)
  // El backend bloquea si hay autoevaluaciones sobre el registro; su mensaje
  // explica cuántas, así que se muestra tal cual.
  if (!resultado.ok) {
    errorForm.value = resultado.error ?? 'No se pudo eliminar'
    return
  }
  await cargar()
}

async function agregarResolucion(r: ProgramaSnies) {
  const resultado = await agregarResolucionService(r.id_programa_snies, {
    tipo_acto: nuevaRes.value.tipo_acto,
    numero: nuevaRes.value.numero || null,
    fecha_ejecutoria: nuevaRes.value.fecha_ejecutoria || null,
    vigencia_anios: nuevaRes.value.vigencia_anios
  })
  if (!resultado.ok) {
    errorForm.value = resultado.error ?? 'No se pudo agregar la resolución'
    return
  }
  nuevaRes.value = { tipo_acto: '', numero: '', fecha_ejecutoria: '', vigencia_anios: null }
  await cargar()
}

async function quitarResolucion(id: number) {
  if (await eliminarResolucionService(id)) await cargar()
}

watch(() => props.idPrograma, () => { expandido.value = null; editando.value = null; cargar() })

onMounted(async () => {
  await cargar()
  const catalogos = await getCatalogosSniesService()
  tiposActo.value = catalogos.tipos_acto
  modalidades.value = catalogos.modalidades
  nivelesFormacion.value = catalogos.niveles_formacion
  await campusStore.cargarTodos()
  campus.value = campusStore.campus as any[]
})
</script>

<style scoped>
.snies-panel { border-top: 1px dashed #d6dee6; margin-top: 0.9rem; padding-top: 0.8rem; }
.sp-cab { display: flex; align-items: center; gap: 0.6rem; }
.sp-cab h4 { margin: 0; font-size: 0.86rem; color: #14507d; }
.sp-conteo { font-size: 0.75rem; color: #9aa5b1; }
.btn-sp-nuevo {
  margin-left: auto; border: 1px solid #c6ddf7; background: #eef5fb; color: #0e69a0;
  border-radius: 6px; padding: 0.2rem 0.55rem; font-size: 0.74rem; font-weight: 600; cursor: pointer;
}
.sp-ayuda { font-size: 0.74rem; color: #6b7280; margin: 0.3rem 0 0.7rem; }
.sp-info, .sp-vacio { font-size: 0.78rem; color: #888; font-style: italic; margin: 0.4rem 0; }

.sp-registro { border: 1px solid #e4eaf0; border-radius: 8px; padding: 0.6rem 0.7rem; margin-bottom: 0.6rem; background: #fbfdff; }
.sp-reg-cab { display: flex; align-items: center; gap: 0.55rem; flex-wrap: wrap; }
.sp-codigo { font-weight: 800; color: #14507d; font-size: 0.85rem; }
.sp-meta { font-size: 0.75rem; color: #4a5b6b; background: #eef2f6; padding: 1px 7px; border-radius: 999px; }
.sp-bandera { font-size: 0.72rem; font-weight: 700; padding: 2px 8px; border-radius: 999px; }
.sp-roja { background: #fdecea; color: #b3261e; }
.sp-amarilla { background: #fff6e5; color: #9a6700; }
.sp-verde { background: #e7f6ec; color: #1d7a3a; }
.sp-sin-dato { background: #f0f2f5; color: #9aa5b1; }
.sp-reg-acciones { margin-left: auto; display: flex; gap: 0.35rem; }
.btn-sp {
  border: 1px solid #d6dee6; background: white; color: #4a5b6b;
  border-radius: 6px; padding: 0.18rem 0.5rem; font-size: 0.72rem; cursor: pointer;
}
.btn-sp:hover { background: #f2f6fa; }
.btn-sp-ok { background: #14507d; color: white; border-color: #14507d; }
.btn-sp-peligro { color: #b3261e; }
.sp-reg-datos { display: flex; gap: 0.8rem; flex-wrap: wrap; font-size: 0.74rem; color: #6b7280; margin-top: 0.4rem; }
.sp-acreditado { color: #1d7a3a; font-weight: 600; }
.sp-primer { color: #0e69a0; font-weight: 600; }

.sp-resoluciones { margin-top: 0.6rem; border-top: 1px solid #eef2f6; padding-top: 0.5rem; }
.sp-tabla { width: 100%; border-collapse: collapse; font-size: 0.74rem; }
.sp-tabla th { text-align: left; color: #6b7280; font-weight: 600; padding: 0.2rem 0.4rem; }
.sp-tabla td { padding: 0.2rem 0.4rem; border-top: 1px solid #f0f3f6; }
.sp-vence { font-weight: 600; padding: 1px 6px; border-radius: 999px; }
.sp-nd { color: #9aa5b1; font-style: italic; }
.btn-quitar-sp { border: none; background: none; color: #c0392b; cursor: pointer; font-size: 0.95rem; line-height: 1; }
.sp-nueva-res { display: flex; gap: 0.4rem; flex-wrap: wrap; margin-top: 0.5rem; }
.sp-ayuda-res { font-size: 0.7rem; color: #9aa5b1; margin: 0.3rem 0 0; }
.sp-input { border: 1px solid #d6dee6; border-radius: 6px; padding: 0.25rem 0.4rem; font-size: 0.75rem; }
.sp-corto { max-width: 6rem; }

.sp-form { border: 1px solid #c6ddf7; background: #f7fbff; border-radius: 8px; padding: 0.8rem; margin-top: 0.6rem; }
.sp-form h5 { margin: 0 0 0.6rem; font-size: 0.82rem; color: #14507d; }
.sp-form-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 0.6rem; }
.sp-form-grid label { display: flex; flex-direction: column; gap: 0.2rem; font-size: 0.74rem; color: #4a5b6b; }
.sp-form-acciones { display: flex; gap: 0.4rem; justify-content: flex-end; margin-top: 0.7rem; }
.sp-error { margin: 0.5rem 0 0; font-size: 0.75rem; color: #b3261e; }
</style>
